import { BehaviorSubject } from 'rxjs'
import { ChannelTrackData, CurrentChannelData } from '../interfaces/channel-track-data'
import { LocalAppLaunchMode, TVApp } from '../interfaces/local-app'
import { LocalChannel } from '../interfaces/local-channel'
import { LocalMediaFile, Media } from 'htv-lib'
import { MonitoringMemoryData, MonitoringStorageData, SystemData } from '../interfaces/monitoring'
import { PowerState } from '../interfaces/power-state'
import { ChannelPosition } from '../pages/channel/channel-position'
import { NetworkData, NetworkState } from '../interfaces/network'
import { StatusSuccessDto } from '../interfaces/dto/status-response-dto'
import { GoogleCastState } from '../interfaces/local-googlecast'
import { ExternalInputData } from '../services/external-input.service'
import { VODVideo } from '../interfaces/vod'

export enum HTVPlatform {
  DEFAULT,
  TIZEN,
  WEBOS,
  PHILIPS,
}

export interface IHTVPlatform {
  id(): string
  init(): Promise<void>
  afterRegistrationHook(deviceId: string | undefined): void
  afterStatusHook(status: StatusSuccessDto): Promise<void>
  enableGuestMode(): Promise<void>
  disableGuestMode(): Promise<void>
  clearData(): Promise<void>
  getSystemData(): Promise<SystemData>
  getPlatformData(): Promise<object>
  getCpuUsage(): Promise<number>
  getMemoryData(): Promise<MonitoringMemoryData>
  getStorageData(): Promise<MonitoringStorageData[]>
  getTemperature(): Promise<number>
  getValue(key: string): any
  setValue(key: string, value: any): void
  removeValue(key: string): void
  clearValues(): void
  reboot(): void
  setLanguage(code: string): Promise<void>
  getPowerState(): Promise<PowerState | undefined>
  setPowerState(state: PowerState): void
  registerPowerStateListener(subject: BehaviorSubject<PowerState | undefined>): void
  getNetworkState(): Promise<NetworkState>
  getNetworkData(): Promise<NetworkData>
  registerNetworkStateListener(subject: BehaviorSubject<NetworkState>): void
  registerApplicationInstalledListener(subject: BehaviorSubject<string[]>): void
  registerApplicationFocusListener(subject: BehaviorSubject<boolean>): void
  getAppList(): Promise<TVApp[]>
  startApp(appId: string): void
  startNetflixApp(appId: string, hotelId: string, launchMode: LocalAppLaunchMode): void
  registerChannelListener(subject: BehaviorSubject<boolean>): void
  startUDPChannel(channel: LocalChannel, pos: ChannelPosition): void
  startWebChannel(channel: LocalChannel, pos: ChannelPosition): void
  startBroadcastChannel(channel: LocalChannel, pos: ChannelPosition): void
  stopUDPChannel(): void
  stopWebChannel(): void
  stopBroadcastChannel(): void
  getCurrentChannel(): Promise<any>
  getUDPCurrentProgramData(): Promise<CurrentChannelData | undefined>
  getWebCurrentProgramData(): Promise<CurrentChannelData | undefined>
  getBroadcastCurrentProgramData(): Promise<CurrentChannelData | undefined>
  setUDPAudioTrack(track: ChannelTrackData): void
  setWebAudioTrack(track: ChannelTrackData): void
  setBroadcastAudioTrack(track: ChannelTrackData): void
  setUDPSubtitleTrack(track: ChannelTrackData): void
  setWebSubtitleTrack(track: ChannelTrackData): void
  setBroadcastSubtitleTrack(track: ChannelTrackData): void
  setSubtitleState(enable: boolean): Promise<boolean>
  getLocalMedias(): Promise<LocalMediaFile[]>
  downloadMedia(media: Media): Promise<LocalMediaFile>
  removeMedia(mediaFile: LocalMediaFile): Promise<void>
  setNativeVolumeDisplay(enabled: boolean): void
  getVolume(): Promise<number>
  setVolume(volume: number): Promise<number>
  setVolumeDown(): Promise<number>
  setVolumeUp(): Promise<number>
  getVolumeMute(): Promise<boolean>
  setVolumeMute(muted: boolean): Promise<boolean>
  getExternalInputs(): Promise<ExternalInputData[]>
  registerExternalInputListener(subject: BehaviorSubject<ExternalInputData[]>): void
  showHDMI(externalInput: ExternalInputData): Promise<void>
  hideHDMI(): Promise<void>
  hasIntegratedGoogleCast(): boolean
  initGoogleCastApp(): void
  registerGoogleCastInitializedListener(subject: BehaviorSubject<boolean | undefined>): void
  disableGoogleCast(): Promise<void>
  initGoogleCastProxy(): Promise<void>
  initGoogleCastSoftAP(): Promise<void>
  acceptGoogleCastTermsOfService(): Promise<void>
  enableGoogleCastUsageReporting(): Promise<void>
  clearGoogleCastData(): Promise<void>
  openGoogleCastLocalPage(
    roomNumber: string,
    supportMessage: string,
    gcStateSubject: BehaviorSubject<GoogleCastState | undefined>
  ): Promise<void>
  openGoogleCastSoftAPPage(roomNumber: string, pairingStatus: boolean, supportMessage: string): Promise<void>
  pollGoogleCastSoftAP(): Promise<any>
  launchGoogleCastSettings(): Promise<void>
  adminOpenAppsSettings(): void
  downloadAndUpgradeApp(deviceId: string): void
  canDownloadAndUpgradeApp(): boolean
  hasCustomVOD(): boolean
  getShakaPlatformConfig(): any
  vodStart(vod: VODVideo, pos: ChannelPosition): void
  vodPlay(vod: VODVideo): void
  vodPause(vod: VODVideo): void
  vodStop(): void
  vodGetCurrentTime(): number
  vodSpeed(vod: VODVideo, speed: number): void
  vodSeekTo(vod: VODVideo, milliseconds: number): Promise<void>
  vodJumpBackward(vod: VODVideo, milliseconds: number): Promise<void>
  vodJumpForward(vod: VODVideo, milliseconds: number): Promise<void>
  initVirtualKeyboard(): void
}
